import React from "react";
import { connect } from "react-redux";
import { List, Button, Space, Typography, Divider, Tag, Radio } from "antd";
import moment from "dayjs";

import { getUserInfo } from "../../../utils/redux/reducers/User";
import Base from "../../Base";
import EditCouponForm from "./EditCouponForm";
import PaymentRewardCampaigns from "./PaymentRewardCampaigns";
import SendCouponForm from "./SendCouponForm";
import DistributedListModal from "./DistributedListModal";
import { FILTER_STATUS } from "../../../constants";

const { Text } = Typography;

const BENEFIT_TYPE_LABELS = {
  threshold_discount: "满减券",
  single_product: "单品券",
  free_shipping: "包邮券",
  payment_reward: "支付有礼券",
};

class CouponsManagement extends Base {
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      data: { data: [] },
      coupon: null,
      selectedCouponId: null,
      selectedCoupon: null,
      filterStatus: FILTER_STATUS.ALL.value,
      showCampaigns: false,
    };
  }

  componentDidMount() {
    super.componentDidMount();
    this.onGetDataList();
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.filterStatus !== this.state.filterStatus) {
      this.onGetDataList();
    }
  }

  onShowAddCouponModal = () => {
    this.setState({
      coupon: { id: null },
    });
  };

  onHideCouponModal = () => {
    this.setState({ coupon: null });
  };

  onSendCoupon(selectedCoupon) {
    this.setState({ selectedCoupon });
  }
  onDistributedList(selectedCouponId) {
    this.setState({
      selectedCouponId,
    });
  }

  onEditCoupon(coupon) {
    this.setState({ coupon });
  }

  onDuplicateCoupon(coupon) {
    this.setState({
      coupon: {
        ...coupon,
        id: null,
        duplicate: true,
      },
    });
  }

  onEditCouponFinished = (values) => {
    const { coupon } = this.state;
    if (coupon && coupon.id) {
      this.onSubmitEditCoupon(values);
    } else {
      this.onAddCoupon(values);
    }
  };

  onSendCoupon2Users = async (users) => {
    const token = this.getUserToken();
    const api = this.api.coupons.send;
    const { selectedCoupon } = this.state;
    if (token) {
      try {
        const apiConfig = {
          token,
          path: { id: selectedCoupon.id },
          data: {
            users: Object.keys(users).map((id) => ({
              user_id: id,
              count: users[id],
            })),
          },
        };
        const res = await api.run(apiConfig);
        if (res.result) {
          this.showMessage(res.message, Base.Message.success);
          this.onHideSendForm();
          this.onGetDataList();
        } else {
          this.showMessage(res.message);
        }
      } catch (error) {
        this.handleApiError(api, error);
      }
    }
  };

  onHideSendForm = () => {
    this.setState({ selectedCoupon: null });
  };

  async onGetDataList(page = 1) {
    const api = this.api.coupons.list;
    if (this.mounted) {
      try {
        this.setState({ loading: true });
        const token = this.getUserToken();
        if (token) {
          const { filterStatus } = this.state;
          const apiConfig = {
            token,
            query: {
              page,
              count: 10,
              "filter[status]": filterStatus,
            },
          };
          const res = await api.run(apiConfig);
          if (res.result) {
            this.setState({
              data: res.data,
            });
          } else {
            this.showMessage(res.message);
          }
        }
      } catch (error) {
        this.handleApiError(api, error);
      } finally {
        if (this.mounted) {
          this.setState({ loading: false });
        }
      }
    }
  }

  async onAddCoupon(data) {
    const token = this.getUserToken();
    const api = this.api.coupons.add;

    if (token) {
      try {
        const apiConfig = {
          token,
          data: this.getCouponData(data),
        };
        const res = await api.run(apiConfig);
        if (res.result) {
          this.showMessage(res.message);
          this.onHideCouponModal();
          this.onGetDataList();
        } else {
          this.showMessage(res.message);
        }
      } catch (error) {
        this.handleApiError(api, error);
      }
    }
  }

  async onSubmitEditCoupon(data) {
    const token = this.getUserToken();
    const api = this.api.coupons.update;
    if (token) {
      try {
        const { coupon } = this.state;
        const apiConfig = {
          path: { id: coupon.id },
          token,
          data: this.getCouponData(data),
        };
        const res = await api.run(apiConfig);
        if (res.result) {
          const { data } = this.state;
          this.showMessage(res.message);
          this.onHideCouponModal();
          this.onGetDataList(data.current_page);
        } else {
          this.showMessage(res.message);
        }
      } catch (error) {
        this.handleApiError(api, error);
      }
    }
  }

  onDeleteCoupon(id) {
    this.showModal(
      this.i18n.t("sales.deleteWarning"),
      Base.Modal.confirm,
      async () => {
        const api = this.api.coupons.delete;
        if (this.mounted) {
          try {
            const token = this.getUserToken();
            if (token) {
              const apiConfig = {
                token,
                path: {
                  id,
                },
              };
              const res = await api.run(apiConfig);
              if (res.result) {
                this.showMessage(res.message, Base.Message.success);
                this.onGetDataList();
              } else {
                this.showMessage(res.message, Base.Message.warning);
              }
            }
          } catch (error) {
            this.handleApiError(api, error);
          } finally {
            this.setState({ loading: false });
          }
        }
      }
    );
  }

  getFormattedDateString(date) {
    if (moment.isDayjs(date)) {
      return date.format("YYYY-MM-DD");
    }
    if (typeof date === "string") {
      return moment(date).format("YYYY-MM-DD");
    }
    return null;
  }

  getCouponData(data) {
    const user = this.getUser();
    const {
      range,
      face_price,
      use_start_price,
      status,
      scope_type,
      scope_ids,
      benefit_type,
      reward_checkout_type,
      reward_popup_image,
      expire_days,
      claim_limit_per_user,
      zh_usage_notice,
      ug_usage_notice,
      ug_name,
      zh_name,
      quantity,
      is_public_coupon,
    } = data;

    const popupImage = Array.isArray(reward_popup_image)
      ? reward_popup_image[0]?.path ||
        reward_popup_image[0]?.file_path ||
        reward_popup_image[0]?.url ||
        ""
      : reward_popup_image || "";

    const isPaymentReward = benefit_type === "payment_reward";
    const startDate = isPaymentReward
      ? this.getFormattedDateString(moment())
      : this.getFormattedDateString(range?.[0]);
    const endDate = isPaymentReward
      ? this.getFormattedDateString(moment().add(10, "year"))
      : this.getFormattedDateString(range?.[1]);

    const payload = {
      ug_name,
      zh_name,
      quantity: isPaymentReward ? 999999 : quantity,
      is_public_coupon: isPaymentReward ? 0 : is_public_coupon,
      expire_days,
      claim_limit_per_user: isPaymentReward ? 0 : claim_limit_per_user,
      zh_usage_notice: zh_usage_notice || "",
      ug_usage_notice: ug_usage_notice || "",
      start_date: startDate,
      end_date: endDate,
      use_merchant_id: user.merchant.id,
      merchant_type: user.merchant.type,
      start_price: (use_start_price || 0) * 100,
      status: status ? 1 : 0,
      coupon_type: "discount",
      benefit_type: benefit_type || "threshold_discount",
      scope_type: scope_type || "order",
      scope_ids: scope_ids || [],
      scope_threshold_type: null,
    };

    if (benefit_type === "threshold_discount" || benefit_type === "single_product") {
      payload.face_price = (face_price || 0) * 100;
    } else if (isPaymentReward) {
      payload.reward_popup_image = popupImage;
      payload.reward_checkout_type = reward_checkout_type || "threshold_discount";
      if (payload.reward_checkout_type === "free_shipping") {
        payload.face_price = 0;
      } else {
        payload.face_price = (face_price || 0) * 100;
      }
    } else {
      payload.face_price = 0;
    }

    return payload;
  }

  render() {
    const {
      loading,
      data,
      coupon,
      selectedCoupon,
      selectedCouponId,
      filterStatus,
      showCampaigns,
    } = this.state;
    return (
      <>
        <List
          bordered
          size="large"
          style={{ backgroundColor: "#fff" }}
          header={
            <Space direction="horizontal" wrap>
              <Button type="primary" onClick={this.onShowAddCouponModal}>
                {this.i18n.t("sales.coupons.add")}
              </Button>
              <Button onClick={() => this.setState({ showCampaigns: true })}>
                支付有礼活动
              </Button>
              <Radio.Group
                style={{ marginRight: 20, marginLeft: 20 }}
                onChange={(e) => {
                  this.setState({ filterStatus: e.target.value });
                }}
                value={filterStatus}
              >
                <Radio value={FILTER_STATUS.ALL.value}>
                  {FILTER_STATUS.ALL.name}
                </Radio>
                <Radio value={FILTER_STATUS.OPENED.value}>
                  {FILTER_STATUS.OPENED.name}
                </Radio>
                <Radio value={FILTER_STATUS.CLOSED.value}>
                  {FILTER_STATUS.CLOSED.name}
                </Radio>
              </Radio.Group>
            </Space>
          }
          itemLayout={window.innerWidth < 1200 ? "vertical" : "horizontal"}
          dataSource={data.data}
          loading={loading}
          pagination={{
            current: data.current_page,
            total: data.total,
            pageSize: 10,
            showQuickJumper: true,
            onChange: (page) => this.onGetDataList(page),
          }}
          renderItem={(item, idx) => (
            <List.Item
              style={{
                backgroundColor: item.status === 1 ? "#ffffff" : "#f1f1f1",
              }}
              actions={[
                <Button
                  type="link"
                  style={{ borderWidth: 0 }}
                  key="list-loadmore-send"
                  onClick={() => this.onSendCoupon(item)}
                >
                  {this.i18n.t("sales.coupons.send")}
                </Button>,
                <Button
                  type="link"
                  style={{ borderWidth: 0 }}
                  key="list-loadmore-send-list"
                  onClick={() => this.onDistributedList(item.id)}
                >
                  {this.i18n.t("sales.coupons.distributedList")}
                </Button>,
                <Button
                  type="link"
                  style={{ borderWidth: 0 }}
                  key="list-loadmore-edit"
                  onClick={() => this.onEditCoupon(item)}
                >
                  {this.i18n.t("product.productItem.actionEdit")}
                </Button>,
                <Button
                  type="link"
                  style={{ borderWidth: 0 }}
                  key="list-loadmore-duplicate"
                  onClick={() => this.onDuplicateCoupon(item)}
                >
                  {this.i18n.t("sales.coupons.duplicate")}
                </Button>,
                <Button
                  type="link"
                  danger
                  style={{ borderWidth: 0 }}
                  key="list-loadmore-more"
                  onClick={() => this.onDeleteCoupon(item.id)}
                >
                  {this.i18n.t("sales.activity.delete")}
                </Button>,
              ]}
            >
              <Text>{item.id}</Text>
              <Divider type="vertical" style={{ height: 20 }} />
              <List.Item.Meta
                style={{ marginLeft: 20, marginRight: 20 }}
                title={this.i18n.getPropNameByLocale(item, "name")}
                description={
                  item.benefit_type === "payment_reward"
                    ? "发放时间与数量由支付有礼活动控制"
                    : `${this.getFormattedDateString(
                        item.start_date
                      )}~${this.getFormattedDateString(item.end_date)}`
                }
              />
              <Space wrap>
                <Tag color="blue">
                  {item.benefit_type === "payment_reward"
                    ? `支付有礼 · ${
                        BENEFIT_TYPE_LABELS[item.reward_checkout_type] ||
                        BENEFIT_TYPE_LABELS.threshold_discount
                      }`
                    : BENEFIT_TYPE_LABELS[item.benefit_type] ||
                      (item.scope_type === "shipping"
                        ? BENEFIT_TYPE_LABELS.free_shipping
                        : BENEFIT_TYPE_LABELS.threshold_discount)}
                </Tag>
                <Space direction="horizontal" wrap>
                  <Text>
                    {this.i18n.t("sales.coupons.total", {
                      count: item.quantity,
                    })}
                  </Text>
                  <Tag color={item.amount_left ? "green" : "red"}>
                    {this.i18n.t("sales.coupons.left", {
                      count: item.amount_left,
                    })}
                  </Tag>
                </Space>
                <Space direction="horizontal" wrap>
                  <Text>
                    {item.start_price
                      ? this.i18n.t("sales.coupons.start", {
                          price: item.start_price / 100,
                        })
                      : this.i18n.t("sales.coupons.no_limit")}
                  </Text>
                  <Text type="danger">
                    {item.benefit_type === "free_shipping" ||
                    (item.benefit_type === "payment_reward" &&
                      item.reward_checkout_type === "free_shipping")
                      ? "满额包邮"
                      : item.benefit_type === "payment_reward"
                        ? this.i18n.t("sales.coupons.face_price", {
                            price: item.face_price / 100,
                          })
                        : this.i18n.t("sales.coupons.face_price", {
                            price: item.face_price / 100,
                          })}
                  </Text>
                </Space>
              </Space>
            </List.Item>
          )}
        />
        <DistributedListModal
          key="distributed-list-modal"
          selectedCouponId={selectedCouponId}
          onCancel={() => {
            this.setState({
              selectedCouponId: null,
            });
          }}
        />
        <EditCouponForm
          visible={coupon !== null}
          onCancel={this.onHideCouponModal}
          onFinish={this.onEditCouponFinished}
          coupon={coupon}
          user={this.props.user}
        />
        <SendCouponForm
          selectedCoupon={selectedCoupon}
          onCancel={this.onHideSendForm}
          onFinish={this.onSendCoupon2Users}
          api={this.api.coupons.users}
        />
        <PaymentRewardCampaigns
          visible={showCampaigns}
          onCancel={() => this.setState({ showCampaigns: false })}
          user={this.props.user}
        />
      </>
    );
  }
}

export default connect((state) => ({
  user: getUserInfo(state),
}))(CouponsManagement);
